import type { AdvancedFilterQuery } from './advanced-filters';
import type { DocumentFilter } from './dataset';

/**
 * Individual diagnosis item with metadata
 */
export interface DiagnosisItem {
    /** SNOMED/OMOP concept name */
    name: string;
    /** ICD-10 code (e.g., "C50.9") */
    icd_code: string | null;
    /** Date when condition was first recorded */
    start_date: string | null;
}

export interface Patient {
    id: string;
    name: string;
    age: number;
    gender: string;
    race?: string;
    ethnicity?: string;
    birthDate?: string;
    /** Structured diagnosis list with ICD codes and dates, sorted newest first */
    diagnosis_list?: DiagnosisItem[];
    /** @deprecated Use diagnosis_list instead */
    diagnosis: string[];
    records: Record[];
    location?: {
        city?: string;
        state?: string;
        zip?: string;
        county?: string;
    };
    provider?: {
        name?: string;
        specialty?: string;
    };
    careSite?: {
        name?: string;
        placeOfService?: string;
    };
    documentCount?: number;
    omopPersonId?: string;
    sourcePatientId?: string;
    /** Where this patient was found: omop=condition_occurrence, es=raw_nlp_annotations_enriched (NLP) */
    sourceFlags?: ('omop' | 'es' | 'es-only')[];
}

export interface Record {
    matched?: boolean; // Flag indicating if this document matched document-level filters
    matchedConditions?: string[]; // Which condition fields this document matched (e.g., ['documentType', 'sentenceContext'])
    id: string;
    name: string;
    type: 'note' | 'lab' | 'imaging' | 'prescription' | 'procedure';
    date: string;
    patientId: string;
    dataSource: string;
    documentType: string;
    noteId: number;
    note_id?: string; // document_id from Elasticsearch (for ingestion datasets) - string format
    // Flag indicating if this document was randomly selected to fill minimum document count
    isRandom?: boolean;
    // Relevance score from DocRanker (0-100), present when docRanker is enabled
    relevanceScore?: number;
    // Provider name (from note.provider relation)
    provider?: string;
    // Visit info (visit type + date from note.visit_occurrence relation)
    visitInfo?: string;
    /** Where this document contributed to the match: omop=structured, es=raw_nlp_annotations_enriched (NLP), es-only=document only in ES (not in OMOP) */
    sourceFlags?: ('omop' | 'es' | 'es-only')[];
}

/** Alias for Record (patient document) to avoid conflict with TypeScript's built-in Record utility type. */
export type PatientDocumentRecord = Record;

export interface DocumentContent {
    id: string;
    title: string;
    content: string;
    date: string;
    documentType: string;
}

export interface PatientSearchFilters {
    includeDocuments?: boolean;
    cohortIds?: string[];
    page?: number;
    pageSize?: number;
    filters?: AdvancedFilterQuery;
    search?: string;
    orderBy?: string;
    orderDirection?: 'asc' | 'desc';
    messageId?: string;
    /** Clinical trial job ID to filter patients by job execution results */
    clinicalTrialJobId?: string;

    randomSampleSize?: number | null;
    /** Random seed for deterministic random selection of patients */
    randomSeed?: number | null;

    documentFilter?: DocumentFilter;
    onlyWithDocuments?: boolean;

    /** Specific patient IDs to filter by (e.g., from clinical trial results) */
    patientIds?: string[];

    /** Ingestion job IDs to filter patients from Elasticsearch */
    ingestionJobIds?: string[];

    /**
     * When true and diagnosis filter is present, also include patients from Elasticsearch NLP matches (chunk_concept_id).
     * ES patient_id maps to OMOP person_source_value.
     */
    includeElasticsearchForFilters?: boolean;
}

export interface CreatePatientRequest {
    person_source_value: string;
    year_of_birth: number;
    gender_source_value?: string;
    month_of_birth?: number;
    day_of_birth?: number;
    birth_datetime?: string;
    race_source_value?: string;
    ethnicity_source_value?: string;
    first_name?: string;
    last_name?: string;
    middle_name?: string;
    street_address?: string;
    city?: string;
    state_province?: string;
    postal_code?: string;
    country?: string;
    telephone?: string;
}

export interface CreatePatientResponse {
    person_id: number;
    person_source_value: string;
    gender_source_value: string;
    year_of_birth: number;
    month_of_birth: number | null;
    day_of_birth: number | null;
    birth_datetime: string | null;
    race_source_value: string;
    ethnicity_source_value: string;
    first_name: string | null;
    last_name: string | null;
    middle_name: string | null;
    street_address: string | null;
    city: string | null;
    state_province: string | null;
    postal_code: string | null;
    country: string | null;
    telephone: string | null;
}
